import { useState } from 'react';
import {
  Alert,
  Icon,
  Modal,
  V2Badge,
  V2Button,
} from '@sakhtar/design-system/react';
import { useAiProvider } from '../connect/AiProviderContext.js';
import { browserPaymentRecoveryStore } from '../connect/paymentRecovery.js';
import { useAiResource } from '../live/useAiResource.js';
import { formatIntegerString, topupAmount } from './pricing.js';
import './buy-credit-modal.css';

export interface BuyCreditModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
  /** Where the gateway returns (the wallet page). Defaults to the current path. */
  readonly walletPath?: string;
}

/**
 * مودال «خرید شارژ هوش مصنوعی»
 * مطابق دیزاین‌سیستم V2 ساختار:
 * - بسته‌های آماده قابل انتخاب با نمایش شفاف قیمت، تعداد توکن و نرخ
 * - فقط سه بستهٔ ثابت فروخته می‌شود؛ قیمت و سهمیهٔ توکن را سرور تعیین می‌کند (ADR-021)
 * - مبلغ دلخواه در حالت توکن خام وجود ندارد، چون قیمت تأییدشده‌ای برایش تعریف نشده است
 * - دکمه اصلی «پرداخت و افزایش اعتبار» با پشتیبانی کامل از وضعیت‌های در حال پردازش و خطا
 */
export function BuyCreditModal({ open, onClose, walletPath }: BuyCreditModalProps) {
  const provider = useAiProvider();
  const packages = useAiResource((signal) => provider.getWalletPackages({ signal }), [provider]);

  const [selectedPackageId, setSelectedPackageId] = useState<string>('pro');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const pkgList = packages.state.status === 'ready' ? packages.state.data.packages : [];
  const selectedPkg =
    pkgList.find((p) => p.id === selectedPackageId) ??
    pkgList.find((p) => p.id === 'pro') ??
    pkgList[0];

  const rawMode = packages.state.status === 'ready' && packages.state.data.pricingMode === 'raw_tokens';

  const validatedAmount =
    selectedPkg && packages.state.status === 'ready'
      ? topupAmount(selectedPkg.amountToman, packages.state.data.bounds)
      : null;

  const canPay = !submitting && packages.state.status === 'ready' && selectedPkg !== undefined;

  const activeTokens = selectedPkg?.tokenCount ? BigInt(selectedPkg.tokenCount) : null;

  const handleSelectPackage = (pkgId: string) => {
    setSelectedPackageId(pkgId);
    setError(null);
  };

  const handleStartPayment = async () => {
    if (!canPay || !validatedAmount || !selectedPkg) return;
    setSubmitting(true);
    setError(null);
    try {
      // A raw-token wallet orders by package id: the server freezes price and quota. Only a
      // legacy cost-plus wallet takes a Toman amount.
      const res = await provider.createTopupCheckout(
        rawMode
          ? { packageId: selectedPkg.id }
          : { amountToman: validatedAmount.amountToman },
      );
      browserPaymentRecoveryStore(walletPath ?? window.location.pathname).remember(res.orderId);
      window.location.href = res.paymentUrl;
    } catch (err: unknown) {
      setSubmitting(false);
      setError(
        err instanceof Error
          ? err.message
          : 'خطا در برقراری ارتباط با درگاه پرداخت. لطفاً دوباره تلاش کنید.',
      );
    }
  };

  return (
    <Modal
      open={open}
      title="خرید شارژ هوش مصنوعی"
      onClose={onClose}
      footer={
        <div className="ai-topup-modal__footer">
          <V2Button
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={submitting}
          >
            انصراف
          </V2Button>
          <V2Button
            variant="primary"
            size="md"
            icon="cart"
            onClick={handleStartPayment}
            disabled={!canPay}
          >
            {submitting ? 'در حال اتصال به درگاه…' : 'پرداخت و افزایش اعتبار'}
          </V2Button>
        </div>
      }
    >
      <div className="ai-topup-modal__content">
        <p className="ai-topup-modal__intro">
          یکی از سه بستهٔ زیر را انتخاب کنید. هر بسته قیمت و تعداد توکن ثابت دارد و سهمیهٔ توکن
          فقط پس از تأیید پرداخت بانکی به کیف پول هوش مصنوعی شرکت افزوده می‌شود.
        </p>

        {packages.state.status === 'error' ? (
          <Alert tone="danger" title="سرویس شارژ در دسترس نیست">
            <p>{packages.state.error.message}</p>
            <div style={{ marginTop: 8 }}>
              <V2Button type="button" size="sm" variant="secondary" onClick={packages.reload}>
                تلاش مجدد
              </V2Button>
            </div>
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="danger" title="خطا در اتصال به درگاه">
            <p>{error}</p>
            <div style={{ marginTop: 8 }}>
              <V2Button
                type="button"
                size="sm"
                variant="primary"
                onClick={handleStartPayment}
                disabled={!canPay}
              >
                تلاش مجدد
              </V2Button>
            </div>
          </Alert>
        ) : null}

        {packages.state.status === 'loading' ? (
          <div className="ai-topup-modal__loading" role="status">
            در حال دریافت بسته‌های شارژ…
          </div>
        ) : (
          <div className="ai-topup-options">
            {/* بسته‌های آماده */}
            <div className="ai-topup-section-title">بسته‌های پیشنهادی</div>
            <div className="ai-topup-pkg-grid" role="radiogroup" aria-label="بسته‌های آماده شارژ">
              {pkgList.map((pkg) => {
                const isSelected = selectedPkg?.id === pkg.id;
                const tokenStr = pkg.tokenCount ? formatIntegerString(pkg.tokenCount) : null;
                return (
                  <button
                    type="button"
                    key={pkg.id}
                    className={`ai-topup-pkg-card ${isSelected ? 'ai-topup-pkg-card--active' : ''}`}
                    onClick={() => handleSelectPackage(pkg.id)}
                    role="radio"
                    aria-checked={isSelected}
                  >
                    <div className="ai-topup-pkg-card__top">
                      <div className="ai-topup-pkg-card__radio" aria-hidden="true">
                        {isSelected ? <span className="ai-topup-pkg-card__radio-inner" /> : null}
                      </div>
                      <span className="ai-topup-pkg-card__title">{pkg.title}</span>
                      {pkg.badge ? (
                        <span className="ai-topup-pkg-card__badge-wrap">
                          <V2Badge tone="brand">{pkg.badge}</V2Badge>
                        </span>
                      ) : null}
                    </div>

                    {tokenStr ? (
                      <div className="ai-topup-pkg-card__tokens">
                        <strong className="sk-num ai-topup-pkg-card__tokens-num">{tokenStr}</strong>
                        <span className="ai-topup-pkg-card__tokens-label">توکن واقعی</span>
                      </div>
                    ) : null}

                    <div className="ai-topup-pkg-card__price">
                      <span className="sk-num ai-topup-pkg-card__price-num">
                        {formatIntegerString(pkg.amountRial)}
                      </span>
                      <span className="ai-topup-pkg-card__price-unit">ریال در درگاه</span>
                    </div>

                    <div className="ai-topup-pkg-card__desc">{pkg.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* خلاصه پیش از پرداخت */}
        {validatedAmount && canPay ? (
          <div className="ai-topup-summary">
            <div className="ai-topup-summary__head">
              <span className="ai-topup-summary__title">خلاصه صورت‌حساب شارژ</span>
              <span className="ai-topup-summary__type">
                {selectedPkg?.title}
              </span>
            </div>

            <div className="ai-topup-summary__grid">
              <div className="ai-topup-summary__row ai-topup-summary__row--accent">
                <span className="ai-topup-summary__label">سهمیهٔ توکن دریافتی:</span>
                <span className="ai-topup-summary__val sk-num">
                  {activeTokens === null ? '—' : formatIntegerString(activeTokens.toString())} توکن
                  واقعی
                </span>
              </div>
              <div className="ai-topup-summary__row">
                <span className="ai-topup-summary__label">مبلغ پرداختی در درگاه بانکی:</span>
                <span className="ai-topup-summary__val sk-num">
                  {formatIntegerString(validatedAmount.amountRial)} ریال
                </span>
              </div>
            </div>

            <div className="ai-topup-summary__foot">
              <Icon name="shield" size="sm" />
              <span>پرداخت امن الکترونیک از طریق شبکهٔ شاپرک (درگاه رسمی زرین‌پال)</span>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
